import { useState } from 'react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { loadStoreModifiers, type PosModifierOption } from '@/utils/posModifiers';

interface Props {
  open: boolean;
  productName: string;
  basePrice: number;
  onClose: () => void;
  onConfirm: (modifiers: PosModifierOption[], extraTotal: number) => void;
  storeId: number;
}

export function PosModifierModal({ open, productName, basePrice, onClose, onConfirm, storeId }: Props) {
  const options = loadStoreModifiers(storeId);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const picked = options.filter((o) => selected.has(o.id));
  const extra = picked.reduce((s, o) => s + o.price, 0);

  return (
    <Modal open={open} onClose={onClose} title={`Modifiers — ${productName}`}>
      <p className="mb-3 text-sm text-slate-500">Base price: {basePrice.toFixed(2)}</p>
      <ul className="space-y-2 mb-4 max-h-60 overflow-y-auto">
        {options.map((o) => (
          <li key={o.id}>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={selected.has(o.id)} onChange={() => toggle(o.id)} />
              <span className="flex-1">{o.name}</span>
              <span className="font-mono">+{o.price.toFixed(2)}</span>
            </label>
          </li>
        ))}
      </ul>
      <p className="mb-4 text-sm font-semibold">Line total: {(basePrice + extra).toFixed(2)}</p>
      <div className="flex gap-2 justify-end">
        <Button variant="secondary" onClick={onClose}>
          Skip
        </Button>
        <Button onClick={() => onConfirm(picked, extra)}>Add to cart</Button>
      </div>
    </Modal>
  );
}
